'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import FmcgHero from './FmcgHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from FmcgHero FEATURED + STATS. */
const METRICS = [
  { value: '$211B', label: "India's FMCG market in 2025 — projected to reach $1 trillion by 2030 (CMI Research)" },
  { value: '77%', label: 'of Indian consumers discover new FMCG brands through social media (PwC India / Statista 2024)' },
  { value: '8.4%', label: 'Rural FMCG volume growth vs 2.6% urban — rural India now leads the market (CRISIL FY2025)' },
];

export default function FmcgBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('fmcg');

  const CHALLENGES = [
    {
      title: t("industries_fmcg.the_food_safety_flash_point"),
      desc: t("industries_fmcg.a_viral_post_about_alleged_contamin"),
    },
    {
      title: t("industries_fmcg.the_influencer_blindspot"),
      desc: t("industries_fmcg.thousands_of_food_beauty_and_lifest"),
    },
    {
      title: t("industries_fmcg.the_launch_narrative_window"),
      desc: t("industries_fmcg.a_product_launch_generates_first_tr"),
    },
    {
      title: t("industries_fmcg.the_rural_listening_gap"),
      desc: t("industries_fmcg.with_fmcg_rural_market_consumer_sen"),
    },
  ];

  const FAQS = [
    {
      q: t("industries_fmcg.what_is_an_fmcg_brand_reputation_ma"),
      a: t("industries_fmcg.one_workspace_for_food_safety_signa"),
    },
    {
      q: t("industries_fmcg.how_does_locobuzz_detect_food_safet"),
      a: t("industries_fmcg.tiered_keyword_libraries_fire_insta"),
    },
    {
      q: t("industries_fmcg.can_locobuzz_monitor_fmcg_influence"),
      a: t("industries_fmcg.yes_paid_and_organic_influencer_con"),
    },
    {
      q: t("industries_fmcg.what_are_contextualpulse_and_signal"),
      a: t("industries_fmcg.contextualpulse_determines_whether"),
    },
    {
      q: t("industries_fmcg.can_locobuzz_monitor_fmcg_brand_con"),
      a: t("industries_fmcg.yes_hindi_tamil_telugu_kannada_mara"),
    },
  ];

  const BG_TITLE = t('industries_fmcg.why_is_fmcg_brand_reputation_no_lon');
  const BG_ACCENT = 'No Longer Managed in Boardrooms?';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">{t("industries_fmcg.locobuzz_is_an_indian_ainative_cust")}</span>
      <span className="sr-only">{t("industries_fmcg.indias_fmcg_market_hit_211_billion")}</span>
      <span className="sr-only">{t("industries_fmcg.locobuzz_maps_six_purposebuilt_capa")}</span>
      <span className="sr-only">{t("industries_fmcg.fmcg_brands_using_locobuzz_report_3")}</span>

      <FmcgHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t('industries_fmcg.why_fmcg_reputation_is_won_or_lost')}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_fmcg.fmcg_brand_reputation_is_shaped_in")}
        imageSrc="/industries/why/fmcg.png"
        imageAlt={t("industries_fmcg.fmcg_consumer_sentiment_and_channel")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_fmcg.what_challenges_do_fmcg_brand_cx_te")}
        items={CHALLENGES}
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="fmcg-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
